import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Swords, Users, Gamepad2, Plus, X, Loader2, Search, Trophy } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import api from "../lib/api";

export default function CreateTournament() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedGame = searchParams.get("game");

  const [loading, setLoading] = useState(false);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(preselectedGame || "");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tournamentType, setTournamentType] = useState("single_elimination");
  const [playerUsernames, setPlayerUsernames] = useState([""]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const response = await api.getGames();
        setGames(response.data);
      } catch (error) {
        console.error("Error fetching games:", error);
      }
    };
    fetchGames();
  }, []);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await api.searchUsers(query);
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  };

  const addPlayer = (username) => {
    if (!playerUsernames.includes(username)) {
      setPlayerUsernames([...playerUsernames.filter(u => u), username]);
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const removePlayer = (index) => {
    setPlayerUsernames(playerUsernames.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedGame) {
      toast.error("Lütfen bir oyun seçin");
      return;
    }
    
    const validUsernames = playerUsernames.filter(u => u.trim());
    if (validUsernames.length < 2) {
      toast.error("En az 2 oyuncu gerekli");
      return;
    }

    setLoading(true);
    try {
      const response = await api.createTournament({
        name,
        game_id: selectedGame,
        tournament_type: tournamentType,
        description,
        player_usernames: validUsernames,
      });
      
      toast.success("Turnuva oluşturuldu!");
      navigate(`/tournament/${response.data.tournament_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Turnuva oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  const selectedGameData = games.find(g => g.game_id === selectedGame);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="create-tournament-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
          Yeni Turnuva Oluştur
        </h1>
        <p className="text-[#B1C1C1] mt-1">Eleme usulü turnuva başlat</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Game Selection */}
        <div className="glass-card p-6">
          <Label className="text-[#C6A87C] text-sm uppercase tracking-wider">Oyun Seç</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            {games.map((game) => (
              <button
                key={game.game_id}
                type="button"
                onClick={() => setSelectedGame(game.game_id)}
                className={`p-4 rounded-lg border text-center transition-all ${
                  selectedGame === game.game_id
                    ? "border-[#4DFFFF] bg-[#4DFFFF]/10"
                    : "border-white/10 hover:border-white/30"
                }`}
                data-testid={`select-game-${game.game_id}`}
              >
                <div 
                  className="w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center"
                  style={{ backgroundColor: `${game.color}33` }}
                >
                  <Gamepad2 className="w-5 h-5" style={{ color: game.color }} />
                </div>
                <p className="text-sm text-white font-medium">{game.name_tr || game.name}</p>
                <p className="text-xs text-[#64748B]">{game.min_players}-{game.max_players} kişi</p>
              </button>
            ))}
          </div>
        </div>

        {/* Tournament Type */}
        <div className="glass-card p-6">
          <Label className="text-[#C6A87C] text-sm uppercase tracking-wider">Turnuva Tipi</Label>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <button
              type="button"
              onClick={() => setTournamentType("single_elimination")}
              className={`p-6 rounded-lg border text-center transition-all ${
                tournamentType === "single_elimination"
                  ? "border-[#4DFFFF] bg-[#4DFFFF]/10"
                  : "border-white/10 hover:border-white/30"
              }`}
              data-testid="single-elimination-btn"
            >
              <Swords className="w-8 h-8 mx-auto mb-3 text-[#C6A87C]" />
              <p className="text-white font-bold">Tek Eleme</p>
              <p className="text-xs text-[#64748B] mt-1">Kaybeden elenir</p>
            </button>
            <button
              type="button"
              onClick={() => setTournamentType("double_elimination")}
              className={`p-6 rounded-lg border text-center transition-all ${
                tournamentType === "double_elimination"
                  ? "border-[#4DFFFF] bg-[#4DFFFF]/10"
                  : "border-white/10 hover:border-white/30"
              }`}
              data-testid="double-elimination-btn"
            >
              <Trophy className="w-8 h-8 mx-auto mb-3 text-[#C6A87C]" />
              <p className="text-white font-bold">Çift Eleme</p>
              <p className="text-xs text-[#64748B] mt-1">İki kez kaybeden elenir</p>
            </button>
          </div>
        </div>

        {/* Tournament Details */}
        <div className="glass-card p-6 space-y-4">
          <div>
            <Label htmlFor="name" className="text-[#B1C1C1]">Turnuva Adı</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn: Yılsonu Şampiyonası"
              className="mt-2 bg-[#050E2E]/50 border-white/10 text-white h-12"
              required
              data-testid="tournament-name-input"
            />
          </div>
          
          <div>
            <Label htmlFor="description" className="text-[#B1C1C1]">Açıklama (Opsiyonel)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Turnuva hakkında kısa bir açıklama..."
              className="mt-2 bg-[#050E2E]/50 border-white/10 text-white"
              rows={3}
            />
          </div>
        </div>

        {/* Players */}
        <div className="glass-card p-6">
          <Label className="text-[#C6A87C] text-sm uppercase tracking-wider">Oyuncular</Label>
          
          {/* Search */}
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Kullanıcı adı ara..."
              className="pl-10 bg-[#050E2E]/50 border-white/10 text-white h-12"
              data-testid="player-search-input"
            />
            {searching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4DFFFF] animate-spin" />
            )}
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-2 glass-card rounded-lg overflow-hidden">
                {searchResults.map((user) => (
                  <button
                    key={user.user_id}
                    type="button"
                    onClick={() => addPlayer(user.username)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#4DFFFF]/20 flex items-center justify-center">
                      <span className="text-sm font-bold text-[#4DFFFF]">
                        {user.name?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-white text-sm">{user.name}</p>
                      <p className="text-xs text-[#64748B]">@{user.username}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected Players */}
          <div className="mt-4 space-y-2">
            {playerUsernames.filter(u => u).map((username, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-3 rounded-lg bg-white/5"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#C6A87C]/20 flex items-center justify-center">
                    <Users className="w-4 h-4 text-[#C6A87C]" />
                  </div>
                  <span className="text-white">@{username}</span>
                </div>
                <button
                  type="button"
                  onClick={() => removePlayer(index)}
                  className="p-1 hover:bg-white/10 rounded"
                >
                  <X className="w-4 h-4 text-[#64748B]" />
                </button>
              </div>
            ))}
          </div>

          {/* Manual Add */}
          <div className="mt-4 flex gap-2">
            <Input
              placeholder="Kullanıcı adı gir"
              className="bg-[#050E2E]/50 border-white/10 text-white"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (e.target.value.trim()) {
                    addPlayer(e.target.value.trim());
                    e.target.value = "";
                  }
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              className="border-white/20 text-white"
              onClick={(e) => {
                const input = e.target.previousSibling;
                if (input.value.trim()) {
                  addPlayer(input.value.trim());
                  input.value = "";
                }
              }}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <p className="text-xs text-[#64748B] mt-2">
            En az 2 oyuncu gerekli (2'nin katları tercih edilir)
          </p>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading || !selectedGame || !name}
          className="w-full bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold h-12"
          data-testid="create-tournament-submit"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Oluşturuluyor...
            </>
          ) : (
            <>
              <Swords className="w-4 h-4 mr-2" />
              Turnuva Oluştur
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
